import { Alert, Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  useClientShareView,
  sharePhotoUrl,
  shareFileUrl,
  fetchShareUpdatesPage,
} from '../api/publicShare';
import { OverviewLoadingState, ProjectOverviewReport } from '../components/ProjectOverviewReport';

/**
 * Public, read-only progress dashboard a client opens from a share link.
 * Renders outside the authenticated shell — everything here comes from the
 * sanitized /api/public/shares/:token payload (no money, no internal data).
 * The dashboard itself is the shared ProjectOverviewReport (also used by the
 * authenticated client portal).
 */
export function ClientSharePage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = useClientShareView(token);

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
        <OverviewLoadingState />
      </Box>
    );
  }
  if (error || !data || !token) {
    const gone = (error as Error | undefined)?.message === 'SHARE_NOT_FOUND';
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Alert severity={gone ? 'warning' : 'error'} sx={{ maxWidth: 480 }}>
          {gone
            ? 'This progress link is no longer active. Please ask your project contact for a new link.'
            : 'Something went wrong loading the report. Please try again in a moment.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <ProjectOverviewReport
        data={data}
        usePhotoSrc={(photoId) => sharePhotoUrl(token, photoId)}
        fetchUpdatesPage={(page, pageSize) => fetchShareUpdatesPage(token, page, pageSize)}
        updatesSurfaceKey={`share:${token}`}
        onDownloadFile={(file) => {
          // The file route is @Public(), so a direct link downloads it.
          const a = document.createElement('a');
          a.href = shareFileUrl(token, file.id);
          a.download = file.originalName;
          a.click();
        }}
      />
    </Box>
  );
}
